// ════════════════════════════════════════════════════════════════════════
//  Die Lehrgänge
//
//  Eine eigene Seite und kein Auswahlmenü im Kopf. Ein Lehrgang ist ein Vorgang
//  mit Zustand – veröffentlichte Fassung, offener Entwurf, stillgelegt oder
//  nicht –, und das steht nicht in ein Dropdown.
// ════════════════════════════════════════════════════════════════════════

import { Link } from "react-router-dom";
import { useSession } from "@otto/plattform";
import { coursePath } from "../../../shared/lib/coursePath.js";
import { whoStarted } from "../../../shared/lib/people.js";
import { useEditorialPeople } from "../../../shared/hooks/useEditorialPeople.js";
import { useCurricula, type Curriculum } from "../lib/data.js";
import { NewCourseForm } from "./NewCourseForm.js";
import { Transfer } from "../../transfer/components/Transfer.js";
import styles from "../../../shared/components/shell.module.css";

/**
 * Der Stand einer Zeile.
 *
 * `wer` kommt fertig herein statt als Kennung: die Auflösung braucht die
 * angemeldete Person und eine Abfrage, und beides hat in einer reinen Funktion
 * nichts zu suchen. Null heißt „dazu ist nichts zu sagen“ – ein Entwurf ohne
 * bekannten Urheber steht als „Entwurf offen“ da, nicht als „Entwurf offen von
 * unbekannt“.
 */
function state(curriculum: Curriculum, wer: string | null): string {
  const parts: string[] = [];
  parts.push(
    curriculum.publishedVersion === null
      ? "noch nie veröffentlicht"
      : `Fassung ${curriculum.publishedVersion} veröffentlicht`,
  );
  if (curriculum.hasDraft) {
    parts.push(wer === null ? "Entwurf offen" : `Entwurf offen von ${wer}`);
  }
  if (curriculum.retiredAt !== null) parts.push("stillgelegt");
  return parts.join(" · ");
}

export function CourseList() {
  const { client, state: session } = useSession();
  const curricula = useCurricula(client);
  const people = useEditorialPeople(client);
  const byId = new Map((curricula.data ?? []).map((c) => [c.id, c]));
  const ich = session.kind === "signedIn" ? session.identity.user.id : "";

  return (
    <>
      {/* Die Marke der Führung sitzt an der KARTE und nicht am „Öffnen“ einer
          Zeile: welche Zeile gemeint ist, weiß die Führung zwar, aber ein Ring
          um genau eine Zeile hiesse, dass diese Komponente den Kontext der
          Führung kennt. Ein Attribut in der Markup bleibt statisch und lesbar –
          dieselbe Überlegung wie beim verworfenen `useTourMark`. */}
      <div className={styles.card} data-tour="lehrgang-oeffnen">
        <h1 className={styles.title}>Lehrgänge</h1>
        <p className={styles.subtitle}>
          Jeder Lehrgang hat seine eigenen Fassungen und seinen eigenen Entwurf. Eine
          Klasse kann mehrere führen; aufeinander aufbauende erst der Reihe nach.
        </p>

        {curricula.isPending && <p className={styles.empty}>Wird geladen …</p>}
        {curricula.isError && (
          <div className={`${styles.notice} ${styles.noticeError}`}>
            Die Lehrgänge konnten nicht geladen werden.
          </div>
        )}

        {curricula.data && curricula.data.length > 0 && (
          <table className={styles.wide}>
            <thead>
              <tr>
                {/* Der übrige Platz gehört dem Lehrgang: Titel und Untertitel
                    stehen dort untereinander und brauchen ihn. */}
                <th className={styles.stretch}>Lehrgang</th>
                <th>Baut auf</th>
                <th>Stand</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {curricula.data.map((curriculum) => (
                <tr key={curriculum.id}>
                  <td className={styles.stretch}>
                    <strong>{curriculum.title}</strong>
                    {curriculum.subtitle && (
                      <>
                        <br />
                        <span className={styles.subtitle}>{curriculum.subtitle}</span>
                      </>
                    )}
                  </td>
                  <td>
                    {curriculum.requiresId
                      ? (byId.get(curriculum.requiresId)?.title ?? "–")
                      : "–"}
                  </td>
                  <td>
                    {state(curriculum, whoStarted(curriculum.draftBy, ich, people.data))}
                  </td>
                  <td>
                    <Link to={coursePath(curriculum.slug, "uebersicht")}>Öffnen</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <NewCourseForm />
      <Transfer />
    </>
  );
}
